import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { ApexOptions } from 'apexcharts';
import {
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import Chart from 'react-apexcharts';
import {
  FiCamera,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiDroplet,
  FiEdit2,
  FiPlus,
  FiPrinter,
} from 'react-icons/fi';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';

import { SetToggleThemeContext } from '../../../contexts/SetToggleThemeContext';
import { useCan } from '../../../hooks/useCan';
import {
  FuelCalibration,
  FuelPump,
  FuelReading,
  FuelReceipt,
  FuelSlot,
  FuelSummaryPumpDay,
  fullPhotoUrl,
  fuelErrorCode,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import {
  SLOT_LABEL,
  WEEKDAYS,
  differenceLevel,
  downloadCsv,
  formatDateTime,
  formatDay,
  formatDeviation,
  formatLiters,
  formatReading,
  isoDay,
  monthLabel,
  monthRange,
  readingsCsv,
  sheetCsv,
  sheetTotals,
  shiftMonth,
} from '../../../utils/fuelReadings';
import { CancelDialog } from './CancelDialog';
import { DieselSheet } from './DieselSheet';
import { FuelShell } from './FuelShell';
import {
  CalibrationsHistory,
  ReadingsHistory,
  ReceiptsHistory,
} from './History';
import { PumpModal } from './PumpModal';

// o projeto tem duas versões dos tipos do react; o apexcharts é tipado com a
// outra. o apelido evita o erro de jsx sem desligar a checagem.
const ApexChart = Chart as unknown as (
  props: Record<string, unknown>,
) => ReactElement;

const PRINT_STYLE =
  '@page { size: A4 landscape; margin: 8mm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }';

interface PhotoView {
  title: string;
  url: string | null;
  caption: ReactNode;
}

function Stat({
  label,
  value,
  help,
  color,
}: {
  label: string;
  value: ReactNode;
  help?: string;
  color?: string;
}) {
  return (
    <Box
      bg="gray.800"
      borderRadius="12px"
      p="4"
      borderWidth="1px"
      borderColor="gray.700"
    >
      <Text fontSize="sm" color="gray.500">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="bold" color={color}>
        {value}
      </Text>
      {help && (
        <Text fontSize="xs" color="gray.500">
          {help}
        </Text>
      )}
    </Box>
  );
}

// situação de um turno de hoje no cartão da bomba. sem leitura e sem
// liberação, o supervisor/ti vê o botão "liberar" (também antes do horário,
// para o feriado)
function SlotStatus({
  row,
  slot,
  release,
  onRelease,
}: {
  row: FuelSummaryPumpDay | undefined;
  slot: FuelSlot;
  release?: FuelReading;
  onRelease?: () => void;
}) {
  const value = slot === 'abertura' ? row?.opening : row?.closing;
  const at = slot === 'abertura' ? row?.opening_at : row?.closing_at;

  if (value !== null && value !== undefined) {
    return (
      <Tag colorScheme="green">
        {SLOT_LABEL[slot]}: {formatReading(value)} · {(at || '').slice(11, 16)}
      </Tag>
    );
  }

  if (release) {
    return (
      <Tooltip bg="gray.650" label={release.notes || ''}>
        <Tag colorScheme="blue">
          {SLOT_LABEL[slot]}: liberado · {release.access_name || '—'}{' '}
          {release.read_at.slice(11, 16)}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <>
      <Tag colorScheme={row?.missing.includes(slot) ? 'orange' : 'gray'}>
        {SLOT_LABEL[slot]}:{' '}
        {row?.missing.includes(slot) ? 'falta' : 'aguardando'}
      </Tag>
      {onRelease && (
        <Button
          size="xs"
          variant="outline"
          colorScheme="blue"
          onClick={onRelease}
        >
          Liberar
        </Button>
      )}
    </>
  );
}

// painel do posto: a folha controle diesel do mês, preenchida pelo sistema e
// conferida com o gescooper, mais o histórico com as fotos
export default function FuelReadingsPanel() {
  const { toggleTheme } = useContext(SetToggleThemeContext);
  const canView = useCan({ permissions: ['fuel_reading_view'] });
  const canCreate = useCan({ permissions: ['fuel_reading_create'] });
  const isAdmin = useCan({ permissions: ['user_adm'] });
  const canRelease =
    useCan({ permissions: ['fuel_reading_release'] }) || isAdmin;
  const allowed = canView || isAdmin;
  const queryClient = useQueryClient();
  const [releasing, setReleasing] = useState<{
    pump: FuelPump;
    slot: FuelSlot;
  } | null>(null);

  const [month, setMonth] = useState(() => isoDay(new Date()).slice(0, 7));
  const [pumpFilter, setPumpFilter] = useState('');
  const [photoView, setPhotoView] = useState<PhotoView | null>(null);
  const [editing, setEditing] = useState<FuelPump | undefined>();
  const pumpModal = useDisclosure();
  const printRef = useRef<HTMLDivElement>(null);

  const range = monthRange(month);
  const summary = useQuery(
    ['fuel-readings', 'summary', range.from, range.to],
    () => fuelReadingsService.summary(range.from, range.to),
    { enabled: allowed },
  );
  const readings = useQuery(
    ['fuel-readings', 'readings', range.from, range.to],
    () =>
      fuelReadingsService.readings({
        from: range.from,
        to: range.to,
        limit: 3000,
      }),
    { enabled: allowed },
  );
  const receipts = useQuery(
    ['fuel-readings', 'receipts', range.from, range.to],
    () =>
      fuelReadingsService.receipts({
        from: range.from,
        to: range.to,
        limit: 3000,
      }),
    { enabled: allowed },
  );
  const calibrations = useQuery(
    ['fuel-readings', 'calibrations', range.from, range.to],
    () =>
      fuelReadingsService.calibrations({
        from: range.from,
        to: range.to,
        limit: 3000,
      }),
    { enabled: allowed },
  );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Controle diesel ${monthLabel(month).replace('/', ' ')}`,
    pageStyle: PRINT_STYLE,
  });

  const { data } = summary;
  const pumps = useMemo(() => data?.pumps || [], [data]);
  const days = useMemo(() => data?.days || [], [data]);
  const today = isoDay(new Date());
  const isCurrentMonth = today.startsWith(month);

  // bombas da folha: as ativas e as que tiveram leitura no mês
  const pumpIds = useMemo(
    () =>
      pumps
        .filter(
          pump =>
            pump.active ||
            days.some(day =>
              day.pumps.some(
                row =>
                  row.pump_id === pump.id &&
                  (row.opening !== null || row.closing !== null),
              ),
            ),
        )
        .map(pump => pump.id),
    [pumps, days],
  );

  const totals = useMemo(() => {
    const sheet = sheetTotals(days, pumpIds);
    const conciliated = days.filter(day => day.difference !== null);

    return {
      ...sheet,
      conciliatedDays: conciliated.length,
      conciliatedErp: conciliated.reduce(
        (sum, day) => sum + (day.erp_out || 0),
        0,
      ),
      missing: days.reduce((sum, day) => sum + day.missing, 0),
      expected: days.reduce((sum, day) => sum + day.expected, 0),
      // nf de venda acima de 500 l (ponto de abastecimento)
      largeSales: days.flatMap(day =>
        day.large_sales.map(item => ({ ...item, day: day.day })),
      ),
    };
  }, [days, pumpIds]);

  const visibleReadings = (readings.data || []).filter(
    item => !pumpFilter || item.pump_id === pumpFilter,
  );
  const todayRow = days.find(day => day.day === today);

  // liberação válida de hoje (sem número) de uma bomba e turno
  const releaseOf = (pumpId: string, slot: FuelSlot) =>
    (readings.data || []).find(
      item =>
        item.reading === null &&
        !item.canceled_at &&
        item.pump_id === pumpId &&
        item.slot === slot &&
        item.read_at.startsWith(today),
    );

  const confirmRelease = async (reason: string) => {
    if (!releasing) {
      return;
    }

    try {
      await fuelReadingsService.release({
        pump_id: releasing.pump.id,
        slot: releasing.slot,
        reason,
      });
      await queryClient.invalidateQueries('fuel-readings');
    } catch (err) {
      // alguém liberou ou lançou antes: atualiza o painel e mostra a mensagem
      const code = fuelErrorCode(err);

      if (code === 'RELEASE_DUPLICATE' || code === 'RELEASE_ALREADY_READ') {
        await queryClient.invalidateQueries('fuel-readings');
      }

      throw err;
    }
  };

  // o gráfico vai só até hoje (o resto do mês ainda não aconteceu)
  const chartDays = days.filter(day => day.day <= today);
  const chartOptions: ApexOptions = {
    chart: {
      stacked: true,
      toolbar: { show: false },
      background: 'transparent',
    },
    theme: { mode: toggleTheme === 'dark' ? 'dark' : 'light' },
    xaxis: { categories: chartDays.map(day => day.day.slice(8, 10)) },
    yaxis: { labels: { formatter: value => formatLiters(value) } },
    dataLabels: { enabled: false },
    // colunas por bomba e a linha das saídas no sistema por cima
    stroke: {
      width: [...pumpIds.map(() => 0), ...(data?.erp.available ? [3] : [])],
    },
    legend: { position: 'top' },
    tooltip: { y: { formatter: value => `${formatLiters(value)} L` } },
    colors: ['#3182CE', '#38B2AC', '#DD6B20'],
  };
  const chartSeries = [
    ...pumpIds.map(id => ({
      name: pumps.find(pump => pump.id === id)?.name || 'Bomba',
      type: 'column',
      data: chartDays.map(
        day => day.pumps.find(item => item.pump_id === id)?.net || 0,
      ),
    })),
    ...(data?.erp.available
      ? [
          {
            name: 'Saídas no sistema',
            type: 'line',
            data: chartDays.map(day => day.erp_out || 0),
          },
        ]
      : []),
  ];

  const showReadingPhoto = (item: FuelReading) =>
    setPhotoView({
      title: `${item.pump_name} · ${SLOT_LABEL[item.slot]} · ${formatDateTime(
        item.read_at,
        true,
      )}`,
      url: item.photo_url,
      caption: (
        <>
          Totalizador lançado: <b>{formatReading(item.reading)}</b> por{' '}
          {item.access_name || '—'}
        </>
      ),
    });

  const showReceiptPhoto = (item: FuelReceipt) =>
    setPhotoView({
      title: `NF ${item.document} · descarga em ${formatDay(item.received_on)}`,
      url: item.photo_url,
      caption: (
        <>
          {formatLiters(item.liters)} L, anotada por {item.access_name || '—'}
        </>
      ),
    });

  const showCalibrationPhoto = (item: FuelCalibration) =>
    setPhotoView({
      title: `Aferição ${item.pump_name} · ${formatDateTime(
        item.calibrated_at,
        true,
      )}`,
      url: item.photo_url,
      caption: (
        <>
          {formatLiters(item.volume_liters)} L, régua{' '}
          <b>{formatDeviation(item.deviation_ml)}</b> (
          {item.approved ? 'aprovada' : 'reprovada'})
        </>
      ),
    });

  const exportSheet = () => {
    if (data) {
      downloadCsv(
        sheetCsv(data, pumpIds, month),
        `controle_diesel_${month}.csv`,
      );
    }
  };

  const exportReadings = () => {
    // endereço da api para o link da foto na planilha
    const origin = (fullPhotoUrl('/') || '/').slice(0, -1);

    downloadCsv(
      readingsCsv(visibleReadings, origin),
      `leituras_encerrante_${month}.csv`,
    );
  };

  if (!allowed) {
    return (
      <FuelShell>
        <Alert status="warning" color="orange.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Você não tem permissão para ver o painel do posto.
            {canCreate && (
              <Button as={Link} to="/fuel-readings/new" size="sm" ml="3">
                Lançar leitura
              </Button>
            )}
          </AlertDescription>
        </Alert>
      </FuelShell>
    );
  }

  return (
    <FuelShell>
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gridGap="3"
        mb="5"
      >
        <Box>
          <Flex align="center" gridGap="2">
            <Icon as={FiDroplet} color="blue.500" boxSize="6" />
            <Heading size="lg">Posto de combustível</Heading>
          </Flex>
          <Text color="gray.500">
            Controle diesel (8607): leitura das bombas às 7h e às 17h, aferição
            e conciliação com o GesCooper.
          </Text>
        </Box>
        <Flex gridGap="2" wrap="wrap">
          {(canCreate || isAdmin) && (
            <Button
              as={Link}
              to="/fuel-readings/new"
              colorScheme="blue"
              color="white"
              leftIcon={<FiCamera />}
            >
              Lançar leitura
            </Button>
          )}
          <Button
            leftIcon={<FiPrinter />}
            onClick={handlePrint}
            isDisabled={!data}
          >
            Imprimir folha
          </Button>
          <Button
            leftIcon={<FiDownload />}
            onClick={exportSheet}
            isDisabled={!data}
          >
            Exportar Excel
          </Button>
        </Flex>
      </Flex>

      <Flex gridGap="2" mb="5" align="center" wrap="wrap">
        <IconButton
          aria-label="Mês anterior"
          icon={<FiChevronLeft />}
          onClick={() => setMonth(shiftMonth(month, -1))}
        />
        <Input
          type="month"
          value={month}
          onChange={event => event.target.value && setMonth(event.target.value)}
          w="180px"
          aria-label="Mês"
        />
        <IconButton
          aria-label="Próximo mês"
          icon={<FiChevronRight />}
          onClick={() => setMonth(shiftMonth(month, 1))}
          isDisabled={isCurrentMonth}
        />
        {!isCurrentMonth && (
          <Button variant="ghost" onClick={() => setMonth(today.slice(0, 7))}>
            Mês atual
          </Button>
        )}
      </Flex>

      {summary.isLoading && (
        <Center py="16">
          <Spinner size="xl" color="blue.500" />
        </Center>
      )}

      {summary.isError && (
        <Alert status="error" color="red.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription flex="1">
            Não foi possível carregar o painel.
          </AlertDescription>
          <Button size="sm" onClick={() => summary.refetch()}>
            Tentar de novo
          </Button>
        </Alert>
      )}

      {data && (
        <>
          <Stack spacing="2" mb="4">
            {data.erp.error && (
              <Alert status="warning" color="orange.900" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  {data.erp.error} As leituras das bombas aparecem normalmente,
                  só falta a coluna do sistema.
                </AlertDescription>
              </Alert>
            )}
            {data.pending_receipts.map(item => {
              const late = Math.round(
                (new Date(`${today}T12:00:00`).getTime() -
                  new Date(`${item.received_on}T12:00:00`).getTime()) /
                  86400000,
              );

              return (
                <Alert
                  key={item.id}
                  status={late > 0 ? 'error' : 'warning'}
                  color={late > 0 ? 'red.900' : 'orange.900'}
                  borderRadius="md"
                >
                  <AlertIcon />
                  <AlertDescription>
                    NF {item.document} ({formatLiters(item.liters)} L),
                    descarregada {late > 0 ? `há ${late} dia(s)` : 'hoje'} (
                    {formatDay(item.received_on)}), ainda não deu entrada no
                    GesCooper. Enquanto não entrar, o saldo do diesel no sistema
                    fica errado.
                  </AlertDescription>
                </Alert>
              );
            })}
            {pumps
              .filter(pump => pump.active)
              .map(pump => {
                if (pump.last_calibration_approved === false) {
                  return (
                    <Alert
                      key={pump.id}
                      status="error"
                      color="red.900"
                      borderRadius="md"
                    >
                      <AlertIcon />
                      <AlertDescription>
                        {pump.name}: a última aferição (
                        {formatDateTime(pump.last_calibration_at)}) foi
                        reprovada. A bomba precisa ser calibrada.
                      </AlertDescription>
                    </Alert>
                  );
                }

                if (pump.calibration_overdue) {
                  return (
                    <Alert
                      key={pump.id}
                      status="warning"
                      color="orange.900"
                      borderRadius="md"
                    >
                      <AlertIcon />
                      <AlertDescription>
                        {pump.name}:{' '}
                        {pump.last_calibration_at
                          ? `última aferição em ${formatDateTime(
                              pump.last_calibration_at,
                              true,
                            ).slice(0, 10)}`
                          : 'nenhuma aferição registrada'}
                        . O procedimento pede pelo menos uma por mês.
                      </AlertDescription>
                    </Alert>
                  );
                }

                return null;
              })}
          </Stack>

          <Tabs variant="enclosed" isLazy>
            <TabList overflowX="auto" overflowY="hidden">
              <Tab>Controle Diesel</Tab>
              <Tab>Resumo</Tab>
              <Tab>Leituras ({readings.data?.length ?? '…'})</Tab>
              <Tab>Aferições ({calibrations.data?.length ?? '…'})</Tab>
              <Tab>Recebimentos ({receipts.data?.length ?? '…'})</Tab>
              {isAdmin && <Tab>Bombas</Tab>}
            </TabList>
            <TabPanels>
              <TabPanel px="0">
                <Box bg="gray.800" borderRadius="12px" p="3">
                  <DieselSheet
                    summary={data}
                    pumpIds={pumpIds}
                    month={month}
                    readings={readings.data}
                    onPhoto={showReadingPhoto}
                  />
                </Box>
                <Text fontSize="xs" color="gray.500" mt="2">
                  Entrada de diesel = o que o frentista anotou na descarga;
                  Sistema = o que foi lançado no GesCooper. Clique no número da
                  leitura para ver a foto. * = abertura diferente do fechamento
                  anterior. “falta” = leitura que já devia ter sido feita. A
                  diferença só aparece quando as leituras das bombas do dia
                  estão completas.
                </Text>
              </TabPanel>

              <TabPanel px="0">
                <SimpleGrid columns={{ base: 2, lg: 5 }} spacing="3" mb="5">
                  <Stat
                    label="Total bombeado no mês"
                    value={`${formatLiters(totals.net)} L`}
                    help="17h − 7h − aferição, somando as bombas"
                  />
                  <Stat
                    label="Saídas no sistema"
                    value={
                      data.erp.available
                        ? `${formatLiters(totals.erpOut)} L`
                        : '—'
                    }
                    help="Vendas, consumo próprio e transferências no GesCooper"
                  />
                  <Stat
                    label="Diferença (bombas − sistema)"
                    value={
                      totals.conciliatedDays > 0
                        ? `${formatLiters(totals.difference)} L`
                        : '—'
                    }
                    color={
                      differenceLevel(
                        totals.conciliatedDays > 0 ? totals.difference : null,
                        totals.conciliatedErp,
                      ) === 'warning'
                        ? 'orange.400'
                        : undefined
                    }
                    help={`Em ${totals.conciliatedDays} dia(s) com as leituras completas`}
                  />
                  <Stat
                    label="Entradas NF (descargas)"
                    value={`${formatLiters(totals.inLiters)} L`}
                    color={
                      data.pending_receipts.length > 0
                        ? 'orange.400'
                        : undefined
                    }
                    help={`${
                      totals.documents
                    } NF anotada(s) no posto · GesCooper: ${
                      data.erp.available
                        ? `${formatLiters(totals.erpIn)} L`
                        : '—'
                    }`}
                  />
                  <Stat
                    label="Leituras feitas"
                    value={`${totals.expected - totals.missing}/${
                      totals.expected
                    }`}
                    color={totals.missing > 0 ? 'orange.400' : 'green.400'}
                    help={
                      totals.missing > 0
                        ? `${totals.missing} faltando no mês`
                        : 'Nenhuma faltando'
                    }
                  />
                </SimpleGrid>

                {totals.largeSales.length > 0 && (
                  <Alert status="info" borderRadius="md" mb="5">
                    <AlertIcon />
                    <AlertDescription>
                      {totals.largeSales.length} NF(s) de venda acima de 500 L
                      no mês (ponto de abastecimento fatura até 500 L por NF):{' '}
                      {totals.largeSales
                        .slice(0, 12)
                        .map(
                          item =>
                            `${item.document} (${formatLiters(
                              item.liters,
                            )} L, ${formatDay(item.day).slice(0, 5)})`,
                        )
                        .join(', ')}
                      {totals.largeSales.length > 12 && ' e outras'}.
                    </AlertDescription>
                  </Alert>
                )}

                {isCurrentMonth && (
                  <>
                    <Text fontWeight="bold" mb="2">
                      Hoje
                    </Text>
                    <SimpleGrid
                      columns={{ base: 1, md: Math.max(1, pumpIds.length) }}
                      spacing="3"
                      mb="6"
                    >
                      {pumps
                        .filter(pump => pump.active)
                        .map(pump => {
                          const row = todayRow?.pumps.find(
                            item => item.pump_id === pump.id,
                          );

                          return (
                            <Box
                              key={pump.id}
                              bg="gray.800"
                              borderRadius="12px"
                              p="4"
                              borderWidth="1px"
                              borderColor={
                                row?.missing.length ? 'orange.400' : 'gray.700'
                              }
                            >
                              <Text fontWeight="bold" mb="2">
                                {pump.name}
                              </Text>
                              <Flex gridGap="2" wrap="wrap" mb="2">
                                {(['abertura', 'fechamento'] as FuelSlot[]).map(
                                  slot => (
                                    <SlotStatus
                                      key={slot}
                                      row={row}
                                      slot={slot}
                                      release={releaseOf(pump.id, slot)}
                                      onRelease={
                                        canRelease
                                          ? () => setReleasing({ pump, slot })
                                          : undefined
                                      }
                                    />
                                  ),
                                )}
                              </Flex>
                              <Text fontSize="sm" color="gray.500">
                                {row?.net !== null && row?.net !== undefined
                                  ? `Total bombeado hoje: ${formatLiters(
                                      row.net,
                                    )} L · `
                                  : ''}
                                Última aferição:{' '}
                                {pump.last_calibration_at
                                  ? `${formatDateTime(
                                      pump.last_calibration_at,
                                    )} (${
                                      pump.last_calibration_approved
                                        ? 'aprovada'
                                        : 'reprovada'
                                    })`
                                  : 'nenhuma'}
                              </Text>
                            </Box>
                          );
                        })}
                    </SimpleGrid>
                  </>
                )}

                <Box bg="gray.800" borderRadius="12px" p="4">
                  <Text fontWeight="bold" mb="2">
                    Total bombeado por dia x saídas no sistema
                  </Text>
                  <ApexChart
                    options={chartOptions}
                    series={chartSeries}
                    type="line"
                    height={300}
                  />
                </Box>
              </TabPanel>

              <TabPanel px="0">
                <Flex mb="3" gridGap="2" wrap="wrap" justify="space-between">
                  <Select
                    value={pumpFilter}
                    onChange={e => setPumpFilter(e.target.value)}
                    w={{ base: '100%', sm: '240px' }}
                    aria-label="Bomba"
                  >
                    <option value="">Todas as bombas</option>
                    {pumps.map(pump => (
                      <option key={pump.id} value={pump.id}>
                        {pump.name}
                      </option>
                    ))}
                  </Select>
                  <Button
                    leftIcon={<FiDownload />}
                    onClick={exportReadings}
                    isDisabled={visibleReadings.length === 0}
                  >
                    Exportar leituras
                  </Button>
                </Flex>
                {readings.isLoading ? (
                  <Spinner />
                ) : (
                  <ReadingsHistory
                    readings={visibleReadings}
                    onPhoto={showReadingPhoto}
                    canEdit={canRelease}
                  />
                )}
              </TabPanel>

              <TabPanel px="0">
                {calibrations.isLoading ? (
                  <Spinner />
                ) : (
                  <CalibrationsHistory
                    calibrations={calibrations.data || []}
                    onPhoto={showCalibrationPhoto}
                  />
                )}
              </TabPanel>

              <TabPanel px="0">
                {receipts.isLoading ? (
                  <Spinner />
                ) : (
                  <ReceiptsHistory
                    receipts={receipts.data || []}
                    checks={[...data.receipts, ...data.pending_receipts]}
                    erpAvailable={data.erp.available}
                    onPhoto={showReceiptPhoto}
                  />
                )}
              </TabPanel>

              {isAdmin && (
                <TabPanel px="0">
                  <Flex justify="flex-end" mb="3">
                    <Button
                      colorScheme="blue"
                      color="white"
                      leftIcon={<FiPlus />}
                      onClick={() => {
                        setEditing(undefined);
                        pumpModal.onOpen();
                      }}
                    >
                      Nova bomba
                    </Button>
                  </Flex>
                  {pumps.map(pump => (
                    <Box
                      key={pump.id}
                      bg="gray.800"
                      p="10px"
                      borderRadius="6px"
                      mb="10px"
                    >
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Flex align="center" gridGap="2">
                            <Text as="h2">{pump.name}</Text>
                            <Tag
                              size="sm"
                              colorScheme={pump.active ? 'green' : 'red'}
                            >
                              {pump.active ? 'Ativa' : 'Desativada'}
                            </Tag>
                          </Flex>
                          <Text fontSize="sm" color="gray.500">
                            {pump.code} · {pump.fuel_type} ·{' '}
                            {pump.branch || 'sem filial'} · produto{' '}
                            {pump.erp_product_code || '—'} · leituras às{' '}
                            {pump.expected_times.replace(/,/g, ' e ')} (
                            {pump.expected_weekdays
                              .split(',')
                              .filter(Boolean)
                              .map(day => WEEKDAYS[Number(day)])
                              .join(', ')}
                            ) · limite {formatLiters(pump.max_jump_liters)} L
                          </Text>
                        </Box>
                        <Tooltip bg="gray.650" label="Editar">
                          <Button
                            colorScheme="green"
                            color="white"
                            aria-label={`Editar ${pump.name}`}
                            onClick={() => {
                              setEditing(pump);
                              pumpModal.onOpen();
                            }}
                          >
                            <FiEdit2 />
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Box>
                  ))}
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>

          {/* versão em papel da folha, usada só pelo "imprimir" */}
          <Box display="none">
            <DieselSheet
              ref={printRef}
              summary={data}
              pumpIds={pumpIds}
              month={month}
              print
            />
          </Box>
        </>
      )}

      <Modal
        isOpen={!!photoView}
        onClose={() => setPhotoView(null)}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent bg="gray.700">
          <ModalHeader>{photoView?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            {photoView && (
              <>
                <Image
                  src={fullPhotoUrl(photoView.url) || ''}
                  alt={photoView.title}
                  w="100%"
                  borderRadius="8px"
                />
                <Text mt="3">{photoView.caption}</Text>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <PumpModal
        isOpen={pumpModal.isOpen}
        onClose={pumpModal.onClose}
        pump={editing}
      />

      <CancelDialog
        title={
          releasing
            ? `Liberar ${SLOT_LABEL[releasing.slot].toLowerCase()} da ${
                releasing.pump.name
              }`
            : ''
        }
        isOpen={!!releasing}
        onClose={() => setReleasing(null)}
        onConfirm={confirmRelease}
        reasonLabel="Motivo da liberação"
        placeholder="Ex.: feriado, bomba com defeito"
        help="Fica registrado com seu nome e o motivo, e aparece nas ocorrências da folha. Se o frentista lançar a leitura depois, ela vale."
        confirmLabel="Liberar"
        confirmColor="blue"
        errorFallback="Não foi possível liberar o turno."
      >
        <Text>O PC do posto destrava sem a leitura de hoje deste turno.</Text>
      </CancelDialog>
    </FuelShell>
  );
}
